import { traversePropertyUpdate } from "@repo/dynamicEditor/src/helpers/dynamicProperties";
import { keyPair, SerializedFile } from "@repo/models";
import { QuestionType, RepFieldAudit, RepFieldAuditQuestion } from "@repo/models";

export interface IActiveLists {
	formType: keyPair[];
	yesNoNA: keyPair[];
	yesNoNotApplicable: keyPair[];
	yesNo: keyPair[];
	retailShopTypes: keyPair[];
	api: any;
	verbiage?: any;
	locations: keyPair[];
	reps: keyPair[];
	auditType?: keyPair[];
	FieldAuditComplaintTypes?: keyPair[];
}

export interface IFieldAuditState {
	repFieldAudit: RepFieldAudit;
	activeLists: IActiveLists;
}

export function mapResponseToList(repFieldAudit: RepFieldAudit) {
	const responses: Partial<RepFieldAuditQuestion>[] = [];

	if (!repFieldAudit.responseMap) return;

	Object.keys(repFieldAudit.responseMap).forEach((key) => {
		let response: string | number | null | undefined;
		let file: SerializedFile | undefined = undefined;
		const type = repFieldAudit.responseMap[key].Type;

		if (repFieldAudit.responseMap[key] == null) {
			response = null;
		} else if (repFieldAudit.responseMap[key].Id != null) {
			response = parseInt(repFieldAudit.responseMap[key].Id.toString());
		} else if (repFieldAudit.responseMap[key].File != null) {
			file = repFieldAudit.responseMap[key].File;
		} else {
			response = repFieldAudit.responseMap[key].Name;
		}

		responses.push({
			representativeFieldAuditQuestionId: Number(key),
			response: response != null ? response : undefined,
			file: file,
			questionType: type,
		});
	});
	repFieldAudit.responses = responses;
}

export function mapListToResponse(repFieldAudit: RepFieldAudit, activeLists: IActiveLists) {
	const responseMap = new Map<number, { [key: string]: any }>();
	let score = 0;
	let scoreTotal = 0;
	repFieldAudit?.questionGroups?.forEach((groups) => {
		groups.questions.forEach((question) => {
			if (question.response == null && question.file == null) {
				const itemMap = new Map<string, any>();

				if (question.score) {
					itemMap.set("Score", 0);
					itemMap.set("ScoreOutOf", 0);
					itemMap.set("ScoreTotal", question.score);
					itemMap.set("Type", question.questionType);

					responseMap.set(question.representativeFieldAuditQuestionId, Object.fromEntries(itemMap));
				} else {
					itemMap.set("Type", question.questionType);

					responseMap.set(question.representativeFieldAuditQuestionId, Object.fromEntries(itemMap));
				}
				return;
			}

			const itemMap = new Map<string, any>();

			if (
				question.questionType === QuestionType.FreeResponse ||
				question.questionType === QuestionType.Date ||
				question.questionType === QuestionType.Time
			) {
				itemMap.set("Name", question.response);
			} else if (question.questionType === QuestionType.PhotoUpload) {
				itemMap.set("File", question.file);
			} else {
				itemMap.set("Id", Number(question.response));
			}

			if (question.score) {
				scoreTotal += question.score;

				if (activeLists.api[question.representativeFieldAuditQuestionId]?.[0]?.score != null) {
					const newScore = activeLists.api[question.representativeFieldAuditQuestionId].filter(
						(item: any) => item.id.toString() === question.response.toString(),
					)?.[0]?.score;

					score += newScore ?? 0;
					itemMap.set("Score", newScore ?? 0);
				} else if (Number(question.response) === 0) {
					score += question.score;
					itemMap.set("Score", question.score);
				} else {
					itemMap.set("Score", 0);
				}

				itemMap.set("ScoreOutOf", question.score);
				itemMap.set("ScoreTotal", question.score);
			}

			itemMap.set("Type", question.questionType);

			responseMap.set(question.representativeFieldAuditQuestionId, Object.fromEntries(itemMap));
		});
	});

	return { responseMap: Object.fromEntries(responseMap), score, scoreTotal };
}

/**
 * Copies responseMap and each response object in it (the values themselves are shared). Field audit editors
 * hand the editor this copy: the dynamic editor copies its template data one level deep and then sets
 * responseMap.<id>.<field> in place, which would otherwise change the original responses (the saved audit's,
 * so Cancel could not revert answers). Every answer write replaces a field of a response (Id, Name, File,
 * Score, ScoreOutOf), so two levels are enough; unlike a JSON clone this keeps Dates and photo data untouched
 * and costs nothing per edit.
 */
export function copyResponseMap<T extends RepFieldAudit["responseMap"]>(responseMap: T): T {
	const copy = {} as T;
	for (const key of Object.keys(responseMap)) {
		const response = responseMap[key];
		copy[key] = response && typeof response === "object" ? { ...response } : response;
	}
	return copy;
}

/**
 * Score fields as the field audit editors store them: `score` is the earned/possible ratio (0 when nothing
 * is scored yet), `scoreAmount` the earned points and `scoreTotal` the possible points. This is the shape
 * applyFieldAuditBusinessLogic writes, the Score components display, and the server reads on save
 * (Pass = score > 0.7). Use it with mapListToResponse, whose `score` is the earned points.
 */
export function toScoreFields(scoreAmount: number, scoreTotal: number) {
	return { score: scoreTotal > 0 ? scoreAmount / scoreTotal : 0, scoreAmount, scoreTotal };
}

export const applyFieldAuditBusinessLogic = async (
	id: string,
	value: any,
	newState: IFieldAuditState,
	repFieldAudit: RepFieldAudit,
) => {
	const { responseMap } = mapListToResponse(repFieldAudit, newState.activeLists);

	Object.keys(responseMap).forEach((key) => {
		if (!newState.repFieldAudit[key])
			newState = traversePropertyUpdate(`repFieldAudit.${key}`, newState, responseMap[key]);
	});

	if (id.includes("responseMap") && id.includes("Id")) {
		const responseId = id.split(".")[2];
		let total = newState.repFieldAudit.responseMap[responseId]?.ScoreTotal;

		if (!newState.repFieldAudit.responseMap[responseId]?.ScoreTotal) return newState;

		let newScore = 0;

		if (newState.activeLists.api[responseId]?.[0]?.score != null) {
			newScore = newState.activeLists.api[responseId].filter((item: any) => item.id === value)?.at(0)?.score;
		} else if (value === 0) {
			newScore = total;
		} else {
			newScore = 0;
		}

		if (value == null) {
			total = 0;
		}

		newState = traversePropertyUpdate(`repFieldAudit.responseMap.${responseId}.Score`, newState, newScore);
		newState = traversePropertyUpdate(`repFieldAudit.responseMap.${responseId}.ScoreOutOf`, newState, total);
	}

	if (newState.repFieldAudit?.responseMap) {
		let scoreTotal = 0;
		let score = 0;
		Object.keys(newState.repFieldAudit.responseMap).forEach((key) => {
			if (newState.repFieldAudit.responseMap[key]?.ScoreOutOf != null) {
				scoreTotal += newState.repFieldAudit.responseMap[key].ScoreOutOf;
			}

			if (newState.repFieldAudit.responseMap[key]?.Score != null) {
				score += newState.repFieldAudit.responseMap[key].Score;
			}
		});

		newState.repFieldAudit.scoreTotal = scoreTotal;
		newState.repFieldAudit.scoreAmount = score;
		newState.repFieldAudit.score = scoreTotal > 0 ? score / scoreTotal : 0;
	}

	return newState;
};
